import React, { useState } from 'react';
import { useTestQuestionsList } from '@/hooks/queries/test/useQuestionsList';
import { useTestAddQuestions, useTestQuestionUpdate, useTestQuestionDelete } from '@/hooks/mutations/test';
import { Question, QuestionType, QuestionUpdateInput, AddQuestionsInput } from '@/types/tests';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertDialog } from '@/components/AlertDialog';
import Loader from '@/components/Loader';
import { Plus, Edit2, Trash2, ArrowLeft, GripVertical, CheckCircle2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/utils/styleUtils';
import { Pagination } from '@/components/Pagination';

interface TestQuestionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  testId: string;
}

type Translation = { locale: 'uz' | 'ru' | 'en'; text: string };
type OptionForm = {
  id?: string;
  isCorrect: boolean;
  translations: Translation[];
};

export const TestQuestionsModal = ({ isOpen, onClose, testId }: TestQuestionsModalProps) => {
  const { toast } = useToast();
  
  const [view, setView] = useState<'list' | 'form'>('list');
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [page, setPage] = useState(1);

  // Form State
  const [points, setPoints] = useState<number>(1);
  const [questionTranslations, setQuestionTranslations] = useState<Translation[]>([
    { locale: 'uz', text: '' },
    { locale: 'ru', text: '' },
    { locale: 'en', text: '' },
  ]);
  const [options, setOptions] = useState<OptionForm[]>([]);

  // Queries & Mutations
  const { data: questionsData, isLoading, refetch } = useTestQuestionsList({
    testId,
    pageNumber: page,
    pageSize: 20,
  });
  
  const { mutate: addQuestions, isPending: isAdding } = useTestAddQuestions();
  const { mutate: updateQuestion, isPending: isUpdating } = useTestQuestionUpdate();
  const { mutate: deleteQuestion } = useTestQuestionDelete();

  const questionsList: Question[] = questionsData?.data?.data || questionsData?.data || [];

  // --- Handlers ---
  const handleOpenCreate = () => {
    setSelectedQuestion(null);
    setPoints(1);
    setQuestionTranslations([
      { locale: 'uz', text: '' },
      { locale: 'ru', text: '' },
      { locale: 'en', text: '' },
    ]);
    // Default 2 options
    setOptions([
      { isCorrect: true, translations: [{ locale: 'uz', text: '' }, { locale: 'ru', text: '' }, { locale: 'en', text: '' }] },
      { isCorrect: false, translations: [{ locale: 'uz', text: '' }, { locale: 'ru', text: '' }, { locale: 'en', text: '' }] },
    ]);
    setView('form');
  };

  const handleOpenEdit = (q: Question) => {
    setSelectedQuestion(q);
    setPoints(q.points || 1);
    setQuestionTranslations([
      { locale: 'uz', text: q.text || '' },
      { locale: 'ru', text: '' },
      { locale: 'en', text: '' },
    ]);
    
    // Map existing options
    if (q.options && q.options.length > 0) {
      setOptions(q.options.map(opt => ({
        id: opt.id,
        isCorrect: opt.isCorrect,
        translations: [
          { locale: 'uz', text: opt.text || '' },
          { locale: 'ru', text: '' },
          { locale: 'en', text: '' },
        ]
      })));
    } else {
      setOptions([]);
    }
    setView('form');
  };

  const handleAddOption = () => {
    setOptions([
      ...options,
      { isCorrect: false, translations: [{ locale: 'uz', text: '' }, { locale: 'ru', text: '' }, { locale: 'en', text: '' }] }
    ]);
  };

  const handleRemoveOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleOptionTranslationChange = (optIndex: number, locale: 'uz' | 'ru' | 'en', text: string) => {
    const newOptions = [...options];
    newOptions[optIndex].translations = newOptions[optIndex].translations.map(t => 
      t.locale === locale ? { ...t, text } : t
    );
    setOptions(newOptions);
  };

  const handleSetCorrectOption = (optIndex: number) => {
    const newOptions = [...options];
    newOptions.forEach((opt, i) => {
      opt.isCorrect = i === optIndex; // Single choice logic
    });
    setOptions(newOptions);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (options.length < 2) {
      toast({ title: 'Xatolik', description: 'Kamida 2 ta variant bo\'lishi kerak', variant: 'destructive' });
      return;
    }
    if (!options.some(o => o.isCorrect)) {
      toast({ title: 'Xatolik', description: 'To\'g\'ri javobni belgilang', variant: 'destructive' });
      return;
    }

    if (selectedQuestion?.id) {
      const data: QuestionUpdateInput = {
        type: QuestionType.SINGLE_CHOICE,
        points,
        translations: questionTranslations,
        options: options,
      };
      updateQuestion({ questionId: selectedQuestion.id, data }, {
        onSuccess: () => {
          toast({ title: 'Muvaffaqiyatli', description: 'Savol yangilandi' });
          setView('list');
          refetch();
        },
        onError: () => toast({ title: 'Xatolik', description: 'Savol yangilanmadi', variant: 'destructive' })
      });
    } else {
      const data: AddQuestionsInput = {
        questions: [{
          type: QuestionType.SINGLE_CHOICE,
          points,
          translations: questionTranslations,
          options: options,
        }]
      };
      addQuestions({ testId, data }, {
        onSuccess: () => {
          toast({ title: 'Muvaffaqiyatli', description: 'Yangi savol qo\'shildi' });
          setView('list');
          refetch();
        },
        onError: () => toast({ title: 'Xatolik', description: 'Savol qo\'shilmadi', variant: 'destructive' })
      });
    }
  };

  const doDeleteQuestion = () => {
    if (!selectedQuestion?.id) return;
    deleteQuestion(selectedQuestion.id, {
      onSuccess: () => {
        toast({ title: 'Muvaffaqiyatli', description: 'Savol o\'chirildi' });
        setIsDeleteOpen(false);
        refetch();
      },
      onError: () => toast({ title: 'Xatolik', description: 'Savol o\'chirilmadi', variant: 'destructive' })
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col bg-slate-50 p-0 border-0 rounded-2xl">
        
        {/* HEADER */}
        <div className="bg-white pl-6 pr-14 py-4 border-b flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            {view === 'form' && (
              <Button variant="ghost" size="icon" onClick={() => setView('list')} className="size-8 rounded-full">
                <ArrowLeft className="size-4" />
              </Button>
            )}
            <DialogTitle className="text-xl font-bold text-slate-900">
              {view === 'list' ? 'Test savollari' : selectedQuestion ? 'Savolni tahrirlash' : 'Yangi savol yaratish'}
            </DialogTitle>
          </div>
          {view === 'list' && (
            <Button onClick={handleOpenCreate} className="bg-primary hover:bg-primary/90 text-white gap-2 rounded-xl">
              <Plus className="size-4" /> Savol qo'shish
            </Button>
          )}
        </div>

        {/* CONTENT */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
          {view === 'list' ? (
            <div className="space-y-3">
              {isLoading ? (
                <div className="py-12"><Loader /></div>
              ) : questionsList.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-slate-300">
                  <p className="text-slate-500 font-medium">Hozircha savollar yo'q.</p>
                </div>
              ) : (
                questionsList.map((q, index) => (
                  <div key={q.id} className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-start gap-4">
                    <div className="mt-1 flex items-center justify-center size-8 rounded-full bg-blue-50 text-blue-600 font-bold shrink-0 text-sm">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0 space-y-3">
                      <div>
                        <p className="font-medium text-slate-900 text-sm leading-relaxed">{q.text || 'Nomsiz savol'}</p>
                        <p className="text-xs text-slate-400 mt-1">{q.points} ball • {q.type}</p>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                        {q.options?.map((opt, i) => (
                          <div key={opt.id || i} className={cn(
                            "px-3 py-2 rounded-xl text-xs flex items-center gap-2 border",
                            opt.isCorrect ? "bg-emerald-50 border-emerald-200 text-emerald-800 font-medium" : "bg-slate-50 border-slate-100 text-slate-600"
                          )}>
                            {opt.isCorrect && <CheckCircle2 className="size-3.5 text-emerald-500" />}
                            <span className="truncate">{opt.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 ml-2">
                      <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(q)} className="size-8 text-slate-500 hover:text-primary">
                        <Edit2 className="size-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => { setSelectedQuestion(q); setIsDeleteOpen(true); }} className="size-8 text-slate-500 hover:text-red-500">
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
<Pagination currentPage={page} setCurrentPage={setPage} paginationInfo={questionsData?.meta?.pagination ?? questionsData?.data?.meta?.pagination} />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl mx-auto">
              
              <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                <h3 className="text-sm font-bold text-slate-800 border-b pb-2">1. Savol ma'lumotlari</h3>
                <div className="w-1/3 space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Ball</Label>
                  <Input type="number" value={points} onChange={(e) => setPoints(Number(e.target.value))} className="rounded-xl" min={1} required />
                </div>
                
                <Tabs defaultValue="uz" className="w-full pt-2">
                  <TabsList className="grid grid-cols-3 bg-slate-100 p-1 rounded-xl w-full max-w-md">
                    <TabsTrigger value="uz" className="rounded-lg text-xs font-medium">O'zbekcha</TabsTrigger>
                    <TabsTrigger value="ru" className="rounded-lg text-xs font-medium">Русский</TabsTrigger>
                    <TabsTrigger value="en" className="rounded-lg text-xs font-medium">English</TabsTrigger>
                  </TabsList>
                  
                  {(['uz', 'ru', 'en'] as const).map((locale) => {
                    const translation = questionTranslations.find(t => t.locale === locale) || { locale, text: '' };
                    return (
                      <TabsContent key={locale} value={locale} className="pt-3">
                        <Label className="text-xs text-slate-600 mb-1.5 block">Savol matni ({locale.toUpperCase()})</Label>
                        <Input 
                          value={translation.text} 
                          onChange={(e) => {
                            setQuestionTranslations(prev => prev.map(t => t.locale === locale ? { ...t, text: e.target.value } : t));
                          }} 
                          placeholder="Savolni kiriting..."
                          className="rounded-xl"
                        />
                      </TabsContent>
                    );
                  })}
                </Tabs>
              </div>

              <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="text-sm font-bold text-slate-800">2. Javob variantlari</h3>
                  <Button type="button" variant="outline" size="sm" onClick={handleAddOption} className="h-7 text-xs gap-1 rounded-lg">
                    <Plus className="size-3" /> Variant qo'shish
                  </Button>
                </div>

                <div className="space-y-3">
                  {options.map((opt, optIndex) => (
                    <div key={optIndex} className={cn("flex gap-3 p-3 rounded-xl border transition-colors", opt.isCorrect ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200 bg-slate-50")}>
                      <div className="pt-2 shrink-0 flex flex-col items-center gap-3">
                        <div className="flex items-center justify-center size-6 bg-white border rounded shadow-sm text-xs font-bold text-slate-500">
                          {String.fromCharCode(65 + optIndex)}
                        </div>
                        <Switch checked={opt.isCorrect} onCheckedChange={() => handleSetCorrectOption(optIndex)} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <Tabs defaultValue="uz" className="w-full">
                          <div className="flex items-center justify-between mb-2">
                            <TabsList className="grid grid-cols-3 bg-white border border-slate-200 p-0.5 rounded-lg h-8">
                              <TabsTrigger value="uz" className="rounded-md text-[10px] px-2 font-medium">UZ</TabsTrigger>
                              <TabsTrigger value="ru" className="rounded-md text-[10px] px-2 font-medium">RU</TabsTrigger>
                              <TabsTrigger value="en" className="rounded-md text-[10px] px-2 font-medium">EN</TabsTrigger>
                            </TabsList>
                            <Button type="button" variant="ghost" size="icon" onClick={() => handleRemoveOption(optIndex)} className="size-7 text-slate-400 hover:text-red-500 hover:bg-red-50">
                              <Trash2 className="size-3.5" />
                            </Button>
                          </div>
                          
                          {(['uz', 'ru', 'en'] as const).map((locale) => {
                            const translation = opt.translations.find(t => t.locale === locale) || { locale, text: '' };
                            return (
                              <TabsContent key={locale} value={locale} className="mt-0">
                                <Input 
                                  value={translation.text} 
                                  onChange={(e) => handleOptionTranslationChange(optIndex, locale, e.target.value)} 
                                  placeholder={`Variant matni (${locale.toUpperCase()})`}
                                  className="rounded-lg h-9 text-sm bg-white"
                                />
                              </TabsContent>
                            );
                          })}
                        </Tabs>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setView('list')} className="rounded-xl px-6">
                  Bekor qilish
                </Button>
                <Button type="submit" disabled={isAdding || isUpdating} className="rounded-xl px-8 bg-primary text-white">
                  {isAdding || isUpdating ? 'Saqlanmoqda...' : 'Saqlash'}
                </Button>
              </div>

            </form>
          )}
        </div>
      </DialogContent>

      <AlertDialog
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        alertTitle="Savolni o'chirish"
        alertDescription="Ushbu savolni o'chirmoqchimisiz?"
        alertActionTitle="O'chirish"
        alertCancel="Bekor qilish"
        alertActionFunction={doDeleteQuestion}
      />
    </Dialog>
  );
};
